import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [currentCodeSnippet, setCurrentCodeSnippet] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const codeSnippets = [
    `const creativity = () => {
  return innovation + passion;
};`,
    `<div className="digital-magic">
  {dreams.map(idea => 
    <Reality key={idea.id} />
  )}
</div>`,
    `function transform(business) {
  return business
    .addDigitalPresence()
    .optimizePerformance()
    .scaleSuccess();
}`
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentCodeSnippet((prev) => (prev + 1) % codeSnippets?.length);
        setIsAnimating(false);
      }, 500);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-surface to-muted">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-32 bg-primary/20 rounded-lg transform rotate-12 animate-pulse"></div>
        <div className="absolute bottom-32 right-16 w-48 h-24 bg-accent/20 rounded-lg transform -rotate-6 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-trust/20 rounded-full animate-bounce delay-2000"></div>
      </div>
      {/* Code Animation Background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className={`font-mono text-sm text-muted-foreground/30 max-w-md transition-all duration-500 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          <pre className="whitespace-pre-wrap">{codeSnippets?.[currentCodeSnippet]}</pre>
        </div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="space-y-8">
          {/* Main Headline */}
          <div className="space-y-4">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
              Where{' '}
              <span className="text-gradient-brand">Creativity</span>
              <br />
              Meets{' '}
              <span className="text-gradient-brand">Technology</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              We don't just build digital solutions—we craft transformative experiences that elevate your brand and create meaningful connections with your audience.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            {/* <Link to="/contact"> */}
            <Link to="/contact-us">
              <Button
                variant="default"
                size="lg"
                className="bg-conversion hover:bg-conversion/90 text-conversion-foreground brand-hover-lift brand-shadow px-8 py-4"
                iconName="Rocket"
                iconPosition="left"
                iconSize={20}
              >
                Start Your Transformation
              </Button>
            </Link>
            <Link to="/services">
              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-4"
                iconName="Play"
                iconPosition="left"
                iconSize={20}
              >
                Explore Our Services
              </Button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="pt-12 space-y-4">
            <p className="text-sm text-muted-foreground uppercase tracking-wider">
              Trusted by ambitious businesses worldwide
            </p>
            <div className="flex items-center justify-center space-x-8 opacity-60">
              <Icon name="Award" size={24} className="text-accent" />
              <Icon name="Shield" size={24} className="text-trust" />
              <Icon name="Star" size={24} className="text-warning" />
              <Icon name="Zap" size={24} className="text-conversion" />
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={32} className="text-muted-foreground" />
      </div>
    </section>
  );
};

export default HeroSection;